import type { WalletStats as WalletStatsType } from '@/lib/api';
import { formatMoney, formatPercent } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface WalletStatsProps {
    stats: WalletStatsType | null;
    classification: string;
}

export function WalletStatsCard({ stats, classification }: WalletStatsProps) {
    if (!stats) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Wallet Stats</CardTitle>
                </CardHeader>
                <CardContent className="flex items-center justify-center py-8">
                    <p className="text-muted-foreground">No stats available yet</p>
                </CardContent>
            </Card>
        );
    }

    const winRate =
        stats.winCount + stats.lossCount > 0
            ? stats.winCount / (stats.winCount + stats.lossCount)
            : 0;

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Wallet Stats</CardTitle>
                {(classification === 'whale' || classification === 'sharp') && (
                    <Badge variant={classification === 'whale' ? 'whale' : 'sharp'}>
                        {classification === 'whale' ? '🐋' : '🎯'} {classification}
                    </Badge>
                )}
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <StatItem label="Total Volume" value={formatMoney(stats.totalVolume)} />
                    <StatItem
                        label="Realized PnL"
                        value={`${stats.realizedPnl >= 0 ? '+' : ''}${formatMoney(stats.realizedPnl)}`}
                        className={stats.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}
                    />
                    <StatItem label="Win Rate" value={formatPercent(winRate)} />
                    <StatItem label="Markets Traded" value={stats.marketsTraded.toString()} />
                    <StatItem
                        label="Win / Loss"
                        value={
                            <span>
                                <span className="text-green-400">{stats.winCount}</span>
                                {' / '}
                                <span className="text-red-400">{stats.lossCount}</span>
                            </span>
                        }
                    />
                    <StatItem label="Avg Trade Size" value={formatMoney(stats.avgTradeSize)} />
                </div>
            </CardContent>
        </Card>
    );
}

function StatItem({
    label,
    value,
    className,
}: {
    label: string;
    value: React.ReactNode;
    className?: string;
}) {
    return (
        <div className="p-4 rounded-lg bg-muted/50">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</div>
            <div className={`text-lg font-bold font-mono ${className || ''}`}>{value}</div>
        </div>
    );
}

// Keep the old export for backwards compatibility
export { WalletStatsCard as WalletStats };
